import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAnalytics as useAnalyticsContext } from '../contexts/AnalyticsContext';
import { useEnhancedAnalytics } from '../contexts/EnhancedAnalyticsContext';
import { useAuth } from '../contexts/AuthContext';

export const useAnalytics = () => {
  return useAnalyticsContext();
};

// Hook for automatic page tracking
export const usePageTracking = () => {
  const location = useLocation();
  const { trackPageView, isLoaded, consentGiven } = useAnalytics();
  const { trackPageView: trackEnhancedPageView } = useEnhancedAnalytics();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Only track if path changed and we're ready
    if (isLoaded && consentGiven && lastTrackedPath.current !== currentPath) {
      lastTrackedPath.current = currentPath;
      
      const page_title = document.title;
      const page_location = window.location.href;
      const page_path = currentPath;

      // Small delay to ensure page is fully loaded
      const timer = setTimeout(() => {
        // Track for GA4
        trackPageView(page_title, page_location, page_path);
        
        // Track for user journeys (enhanced analytics)
        trackEnhancedPageView({
          page_title,
          page_location,
          page_path
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.search, isLoaded, consentGiven]);
};

// Hook for ecommerce tracking
export const useEcommerceTracking = () => {
  const { trackPurchase, trackAddToCart, trackViewItem, trackBeginCheckout, trackEvent } = useAnalytics();
  const { user } = useAuth();

  // Format item for GA4
  const formatItem = useCallback((product, quantity = 1, variant = null) => {
    return {
      item_id: product.sku || product.id,
      item_name: product.name,
      category: product.category?.name || 'Unknown',
      quantity: quantity,
      price: user?.role === 'b2b' ? product.price_b2b : product.price_b2c,
      item_variant: variant,
      item_brand: product.brand || 'Unknown'
    };
  }, [user]);

  // Track product view
  const trackProductView = useCallback((product) => {
    const item = formatItem(product);
    trackViewItem('ILS', item.price, [item]);
  }, [formatItem, trackViewItem]);

  // Track add to cart
  const trackAddToCartEvent = useCallback((product, quantity = 1) => {
    const item = formatItem(product, quantity);
    const value = item.price * quantity;
    trackAddToCart('ILS', value, [item]);
  }, [formatItem, trackAddToCart]);

  // Track remove from cart
  const trackRemoveFromCart = useCallback((product, quantity = 1) => {
    const item = formatItem(product, quantity);
    const value = item.price * quantity;
    trackEvent('remove_from_cart', {
      currency: 'ILS',
      value,
      items: [item]
    });
  }, [formatItem, trackEvent]);

  // Track begin checkout
  const trackCheckoutBegin = useCallback((items, total) => {
    const formattedItems = items.map(item => formatItem(item.product, item.quantity));
    trackBeginCheckout('ILS', total, formattedItems);
  }, [formatItem, trackBeginCheckout]);

  // Track purchase completion
  const trackPurchaseComplete = useCallback((order) => {
    const items = order.items.map(item => formatItem(item.product, item.quantity));
    trackPurchase(order.order_number, order.total_amount, 'ILS', items);
  }, [formatItem, trackPurchase]);

  // Track search
  const trackSearch = useCallback((search_term, results_count = null) => {
    trackEvent('search', {
      search_term,
      results_count
    });
  }, [trackEvent]);

  // Track user registration
  const trackRegistration = useCallback((method = 'email') => {
    trackEvent('sign_up', {
      method
    });
  }, [trackEvent]);

  // Track user login
  const trackLogin = useCallback((method = 'email') => {
    trackEvent('login', {
      method
    });
  }, [trackEvent]);

  // Track form submission
  const trackFormSubmit = useCallback((form_name, form_id = null) => {
    trackEvent('form_submit', {
      form_name,
      form_id
    });
  }, [trackEvent]);

  // Track file download
  const trackDownload = useCallback ((file_name, file_extension = null) => {
    trackEvent('file_download', {
      file_name,
      file_extension
    });
  }, [trackEvent]);

  // Track video play
  const trackVideoPlay = useCallback((video_title, video_duration = null) => {
    trackEvent('video_play', {
      video_title,
      video_duration
    });
  }, [trackEvent]);

  // Track custom business events
  const trackB2BQuoteRequest = useCallback((product_ids, total_value) => {
    trackEvent('b2b_quote_request', {
      product_ids: product_ids.join(','),
      value: total_value,
      currency: 'ILS'
    });
  }, [trackEvent]);

  const trackB2BContractView = useCallback((contract_id) => {
    trackEvent('b2b_contract_view', {
      contract_id
    });
  }, [trackEvent]);

  const trackWishlistAdd = useCallback((product) => {
    const item = formatItem(product);
    trackEvent('add_to_wishlist', {
      currency: 'ILS',
      value: item.price,
      items: [item]
    });
  }, [formatItem, trackEvent]);

  return {
    // Product tracking
    trackProductView,
    trackAddToCartEvent,
    trackRemoveFromCart,
    
    // Checkout tracking
    trackCheckoutBegin,
    trackPurchaseComplete,
    
    // User actions
    trackSearch,
    trackRegistration,
    trackLogin,
    trackFormSubmit,
    trackDownload,
    trackVideoPlay,
    
    // Business-specific
    trackB2BQuoteRequest,
    trackB2BContractView,
    trackWishlistAdd,
    
    // Utility
    formatItem
  };
};

// Hook for performance tracking
export const usePerformanceTracking = () => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    // Track Core Web Vitals
    const loadWebVitals = async () => {
      try {
        const { getCLS, getFID, getFCP, getLCP, getTTFB } = await import('web-vitals');
        
        getCLS((metric) => {
          trackEvent('web_vital_cls', {
            value: Math.round(metric.value * 1000),
            metric_id: metric.id
          });
        });

        getFID((metric) => {
          trackEvent('web_vital_fid', {
            value: Math.round(metric.value),
            metric_id: metric.id
          });
        });

        getFCP((metric) => {
          trackEvent('web_vital_fcp', {
            value: Math.round(metric.value),
            metric_id: metric.id
          });
        });

        getLCP((metric) => {
          trackEvent('web_vital_lcp', {
            value: Math.round(metric.value),
            metric_id: metric.id
          });
        });

        getTTFB((metric) => {
          trackEvent('web_vital_ttfb', {
            value: Math.round(metric.value),
            metric_id: metric.id
          });
        });
      } catch (error) {
        // Web Vitals not available
      }
    };

    loadWebVitals();

    // Track page load time
    window.addEventListener('load', () => {
      const loadTime = performance.timing.loadEventEnd - performance.timing.navigationStart;
      trackEvent('page_load_time', {
        value: Math.round(loadTime),
        page: window.location.pathname
      });
    });
  }, [trackEvent]);
};

// Hook for error tracking
export const useErrorTracking = () => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    const handleError = (error) => {
      trackEvent('javascript_error', {
        error_message: error.message,
        error_filename: error.filename,
        error_lineno: error.lineno,
        error_colno: error.colno,
        page: window.location.pathname
      });
    };

    const handleUnhandledRejection = (event) => {
      trackEvent('unhandled_promise_rejection', {
        error_message: event.reason?.message || 'Unknown error',
        page: window.location.pathname
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [trackEvent]);

  // Manual error tracking
  const trackError = useCallback((error, context = {}) => {
    trackEvent('manual_error', {
      error_message: error.message || error,
      error_stack: error.stack,
      ...context,
      page: window.location.pathname
    });
  }, [trackEvent]);

  return { trackError };
};

export default useAnalytics;
